import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailFinderQuestion } from '../../../models/TrailFinderQuestion.model'

import UserService from '../../../services/UserService/UserService'
import { TrailFinderQuestionService } from '../../../services/TrailFinderQuestionService/TrailFinderQuestionService'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import Tabs from '../../../components/Tabs/Tabs.component'
import TrailFinderQuestionForm from '../shared/TrailFinderQuestionForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import TrailFinderAnswerSearch from '../../TrailFinderAnswers/TrailFinderAnswerSearch/TrailFinderAnswerSearch.route'
import { useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailFinderQuestionSectionIds, trailFinderQuestionSections } from '../shared/TrailFinderQuestionSections'

const TrailFinderQuestionDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFinder])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailFinderQuestionService = useMemo(() => new TrailFinderQuestionService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail Finder Question Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: trailFinderQuestion } = useQuery(
    [
      'TrailFinderQuestionService.get',
      {
        id: Number(id),
        relations: [],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailFinderQuestionService.get({
        id: Number(id),
        relations: [],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail finder question.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (trailFinderQuestion?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailFinderQuestionService
        .delete(trailFinderQuestion.id)
        .then(() => {
          SnackbarContext.show(
            `Trail Finder Question deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the trailFinderQuestion list page
          setTimeout(() => {
            navigate(`/trail-finder-questions`)
            queryClient.removeQueries([
              'TrailFinderQuestionService.get',
              {
                id: Number(id),
                relations: [],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail Finder Question failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const tabs = useMemo(() => {
    const tabs = []
    if (trailFinderQuestion) {
      tabs.push({
        label: 'Answers',
        content: <TrailFinderAnswerSearch nested trailFinderQuestion={trailFinderQuestion} hideHeader />,
      })
    }
    return tabs
  }, [trailFinderQuestion])

  const sectionById = (sectionId: TrailFinderQuestionSectionIds) =>
    trailFinderQuestionSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailFinderQuestionSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {trailFinderQuestion && (
        <FormDialog<TrailFinderQuestion>
          open={isEditing}
          entity={trailFinderQuestion}
          entityLabel="Trail Finder Question"
          onClose={closeEditor}
        >
          <TrailFinderQuestionForm
            trailFinderQuestion={trailFinderQuestion}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this trail finder question?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trailFinderQuestion && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Trail Finder',
                          href: '/trail-finder-questions',
                        },
                      ]
                      breadcrumbs.push({ label: `Question ID: ${trailFinderQuestion.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={trailFinderQuestion.question}
                  active={trailFinderQuestion.active}
                  subtitle={`${trailFinderQuestion.region} · ID ${trailFinderQuestion.id}`}
                  meta={[
                    `Updated ${formatDate(trailFinderQuestion.updatedAt)}`,
                    `Created ${formatDate(trailFinderQuestion.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailFinderQuestionSectionIds.general)}
                      onEdit={editSectionHandler(TrailFinderQuestionSectionIds.general)}
                    >
                      <DetailField label="Question">{trailFinderQuestion.question || '-'}</DetailField>
                      <DetailFieldGrid>
                        <DetailField label="Visibility">
                          {trailFinderQuestion.active ? 'Active' : 'Inactive'}
                        </DetailField>
                        <DetailField label="Sort Weight">{trailFinderQuestion.sortWeight ?? '-'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailFinderQuestionDetail
